import { Directionality } from "@angular/cdk/bidi";
import { NgIf } from "@angular/common";
import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  Input,
  NgZone,
  Optional,
  Renderer2,
} from "@angular/core";
import { NzButtonComponent } from "ng-zorro-antd/button";
import { NzConfigService } from "ng-zorro-antd/core/config";
import { Subject, startWith, takeUntil } from "rxjs";
import { UiIconDirective } from "../icon/icon.directive";
import { UiIconModule } from "../icon/icon.module";
import { UiButtonShape, UiButtonSize, UiButtonType, UiSpecialButtonType } from "./models/button.models";

@Component({
  selector: "button[ui-button], a[ui-button]",
  exportAs: "uiButton",
  host: {
    "[class.ant-btn-success]": `uiSuccess`,
    "[class.ant-btn-warning]": `uiWarning`,
    "[class.ant-btn-circle]": `nzShape === 'circle'`,
    "[class.special-gray]": `uiSpecialType === 'gray'`,
    "[class.special-teal]": `uiSpecialType === 'teal'`,
    "[class.special-red]": `uiSpecialType === 'red'`,
    "[class.special-blue]": `uiSpecialType === 'blue'`,
    "[attr.aria-disabled]": `!isButtonElement && disabled || null`,
    "[attr.role]": `!isButtonElement ? 'button' : null`,
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span *ngIf="nzLoading" ui-icon uiType="loading"></span>
    <ng-content></ng-content>
  `,
  preserveWhitespaces: false,
  standalone: true,
  imports: [NgIf, UiIconModule],
})
export class UiButtonComponent extends NzButtonComponent implements AfterViewInit, AfterContentInit {
  @Input("uiGhost")
  public nzGhost = false;
  @Input("uiBlock")
  public nzBlock = false;
  @Input("uiDanger")
  public nzDanger = false;
  @Input("uiLoading")
  public nzLoading = false;
  @Input()
  public uiSuccess?: boolean;
  @Input()
  public uiWarning?: boolean;
  @Input()
  public uiSpecialType?: UiSpecialButtonType;
  @Input("uiShape")
  public nzShape: UiButtonShape;
  @Input("uiSize")
  public nzSize: UiButtonSize;
  @Input("uiType")
  public nzType: UiButtonType;
  @Input("tabindex")
  public tabIndex: number | string | null = 0;

  /**
   * @internal
   */
  @ContentChild(UiIconDirective, { read: ElementRef }) public nzIconDirectiveElement!: ElementRef;

  /**
   * @internal
   */
  public thisElementRef: ElementRef<HTMLButtonElement>;

  public get iconOnly(): boolean {
    const listOfNode = Array.from(this.thisElementRef.nativeElement.childNodes || []);
    const noText = listOfNode.every((node) => node.nodeName !== "#text");
    const noSpan =
      listOfNode.filter((node) => {
        return !(node.nodeName === "#comment" || !!(node as HTMLElement)?.attributes?.getNamedItem("ui-icon"));
      }).length == 0;
    return !!this.nzIconDirectiveElement && noSpan && noText;
  }

  public get isButtonElement(): boolean {
    return this.thisElementRef.nativeElement.tagName.toLowerCase() === "button";
  }

  constructor(
    ngZone: NgZone,
    elementRef: ElementRef<HTMLButtonElement>,
    cdr: ChangeDetectorRef,
    private thisRenderer: Renderer2,
    nzConfigService: NzConfigService,
    @Optional() directionality: Directionality
  ) {
    super(ngZone, elementRef, cdr, thisRenderer, nzConfigService, directionality);

    this.thisElementRef = elementRef;
  }

  public ngAfterViewInit(): void {
    // the original method adds empty SPANs for whitespace, but since we use Prettier,
    // we cannot easily omit this whitespace
    const nodes = this.thisElementRef.nativeElement.childNodes;
    for (const node of Array.from(nodes)) {
      if (node.nodeName === "#text" && node.nodeValue.trim() === "") {
        const parent = this.thisRenderer.parentNode(node);
        this.thisRenderer.removeChild(parent, node);
      }
    }

    this.assertIconWithBadgeOnly(this.thisElementRef.nativeElement, this.thisRenderer);

    super.ngAfterViewInit();
  }

  public ngAfterContentInit(): void {
    super.ngAfterContentInit();

    const loading$: Subject<boolean> = this["loading$"];
    const destroy$: Subject<void> = this["destroy$"];
    loading$.pipe(startWith(this.nzLoading), takeUntil(destroy$)).subscribe((loading) => {
      requestAnimationFrame(() => {
        for (const childNode of this.thisElementRef.nativeElement.childNodes) {
          if (childNode.nodeName === "#comment" || childNode.nodeName === "#text") {
            continue;
          }

          // ignore the loading icon
          if ((childNode as HTMLSpanElement).classList?.contains("anticon-loading")) {
            continue;
          }

          if (loading) {
            this.thisRenderer.setStyle(childNode, "display", "none");
          } else {
            this.thisRenderer.removeStyle(childNode, "display");
          }
        }
      });
    });
  }

  public assertIconWithBadgeOnly(element: HTMLButtonElement, renderer: Renderer2): void {
    const listOfNode = Array.from(element.childNodes);
    const noText = listOfNode.every((node) => node.nodeName !== "#text");
    if (!noText) return;

    // ignore icon
    const noSpan = listOfNode
      .filter((node) => {
        const iconChildNodes = Array.from(node.childNodes || []);
        return !(node.nodeName === "SPAN" && iconChildNodes.length > 0 && iconChildNodes.every((ic) => ic.nodeName === "svg"));
      })
      .every((node) => node.nodeName !== "SPAN");
    if (!noSpan) return;

    const iconChildNodesInsideBadge = listOfNode.filter((node) => {
      const iconChildNodes: ChildNode[] = Array.from(node.childNodes || []);
      return (
        node.nodeName === "UI-BADGE" &&
        iconChildNodes.length > 0 &&
        iconChildNodes.filter((childNode) => {
          return childNode instanceof HTMLElement && childNode.attributes?.getNamedItem("ui-icon");
        })
      );
    }).length;
    if (iconChildNodesInsideBadge < 1) return;

    renderer.addClass(element, "ui-btn-icon-with-badge-only");
  }
}
